import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { useIntl } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useTripDocumentUrl } from 'tripPlanner/hooks/api/documents'

const StyledTextButtonWrapper = styled(Group)`
  height: 100%;
  position: relative;
`

const ContentContainer = styled(Group)`
  max-width: ${rem(500)};
`

const ButtonGroup = styled(Group)`
  &.align-bottom {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: ${rem(20)};
  }
`

interface Props {
  documentKey: string
}

function DocumentPdfView({ documentKey }: Props) {
  const intl = useIntl()
  const { data: presignedUrl } = useTripDocumentUrl({ documentKey })

  const modalElement = useModalElementContext<boolean>()
  const closeModal = useCallback(() => {
    modalElement.resolve(false)
  }, [modalElement])

  const isMobile = useScreenSizeOnly('mobile')

  useEffect(() => {
    Analytics.trackClientEvent({
      action: 'view',
      subject: 'trip_document',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    })
  }, [documentKey])

  const openedInNewTab = useRef(false)
  useEffect(() => {
    if (isMobile && presignedUrl && !openedInNewTab.current) {
      window.open(presignedUrl, '_blank')
      openedInNewTab.current = true
      closeModal()
    }
  }, [closeModal, isMobile, presignedUrl])

  return (
    <>
      {!presignedUrl && <LoadingIndicator />}
      {presignedUrl && (
        <object
          data={presignedUrl}
          width="100%"
          height="100%"
          aria-label={intl.formatMessage({
            id: 'kI-ylsZhdCAbTIiF9ZDU',
            defaultMessage: 'document',
            description: 'Accessible label for the embedded PDF document viewer',
          })}
        >
          <StyledTextButtonWrapper
            direction="vertical"
            verticalAlign="center"
            horizontalAlign="center"
          >
            <ContentContainer direction="vertical" gap={8}>
              <HeadingTextBlock variant="heading3" align="center">
                {intl.formatMessage({
                  id: 'xtXzOsvkmcJt3cft4dCC',
                  defaultMessage: 'Unable to display preview',
                  description: 'Heading when PDF document preview cannot be rendered',
                })}
              </HeadingTextBlock>
              <BodyTextBlock variant="medium" align="center">
                {intl.formatMessage({
                  id: 'RBEXGoif_Y7iczPzFjdz',
                  defaultMessage: 'We are unable to display your document. Please use the link below to open it in a new window.',
                  description: 'Message explaining document preview is unavailable with instructions to open in new window',
                })}
              </BodyTextBlock>
              <ButtonGroup
                direction={isMobile ? 'vertical-reverse' : 'horizontal'}
                horizontalAlign="center"
                gap={8}
                className={clsx({ 'align-bottom': isMobile })}
              >
                <TextButton
                  kind="tertiary"
                  onClick={() => closeModal()}
                  fit={isMobile ? 'full-width' : undefined}
                  size={isMobile ? 'large' : undefined}
                >
                  {intl.formatMessage({
                    id: 'dvftlCtdyJdKxD1HPX3g',
                    defaultMessage: 'Cancel',
                    description: 'Button to close the document preview modal in the cancellation flow',
                  })}
                </TextButton>
                <TextButton
                  kind="primary"
                  href={presignedUrl}
                  target="_blank"
                  fit={isMobile ? 'full-width' : undefined}
                  size={isMobile ? 'large' : undefined}
                  startIcon={<LineExternalLinkAltIcon />}
                >
                  {intl.formatMessage({
                    id: 'Xk-PCoW7R9xhyRqk_JoG',
                    defaultMessage: 'View document',
                    description: 'Button to open the document in a new browser window',
                  })}
                </TextButton>
              </ButtonGroup>
            </ContentContainer>
          </StyledTextButtonWrapper>
        </object>
      )}
    </>
  )
}

export default DocumentPdfView
